import { useQuery } from '@tanstack/react-query'
import { fetchGroupSummary } from '@/api/groups'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'
import type { GroupRole } from '@/types'

export interface GroupPermissions {
  role: GroupRole | null
  isCreator: boolean
  canVote: boolean
  canRate: boolean
  canEditContent: boolean
  canManageGroup: boolean
  canDeleteGroup: boolean
}

// Mirrors the backend capability matrix in services/permissions.py. Frontend
// gating is UX-only; the backend remains the enforcement boundary.
export function useGroupPermissions(): GroupPermissions {
  const activeGroupId = useUIStore((s) => s.activeGroupId)
  const rolePreview = useUIStore((s) => s.rolePreview)
  const user = useAuthStore((s) => s.user)
  const isAdminView = useIsAdmin()

  const { data: summary } = useQuery({
    queryKey: ['group-summary', activeGroupId],
    queryFn: () => fetchGroupSummary(activeGroupId!),
    enabled: !!activeGroupId,
  })

  const member = summary?.members.find((m) => m.steamId === user?.steamId)
  let role = member?.role ?? null
  let isCreator = member?.isCreator ?? false

  // Admin-only preview: simulate another role's gating without changing the
  // real membership. Backend still enforces the actual role, so forced calls
  // behave per the admin's true permissions — this only reshapes the UI.
  if (isAdminView && rolePreview !== 'actual') {
    isCreator = rolePreview === 'creator'
    role = rolePreview === 'creator' ? 'admin' : rolePreview
  }

  // Default to the most restrictive result until the summary resolves so no
  // mutation control flashes enabled for a user who lacks the capability.
  const isMember = role !== null
  return {
    role,
    isCreator,
    canVote: isMember,
    canRate: isMember,
    canEditContent: role === 'user' || role === 'admin',
    canManageGroup: role === 'admin',
    canDeleteGroup: isCreator,
  }
}
